import { useEffect, useState } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { ShieldCheck, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyCallback() {
  const [params] = useSearchParams();
  const location = useLocation();
  const [status, setStatus] = useState("working"); // working | ok | error
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Grab state + tokens from either query string or URL hash (implicit flow)
    const hash = location.hash?.startsWith("#") ? location.hash.slice(1) : "";
    const hashParams = new URLSearchParams(hash);
    const state = params.get("state") || hashParams.get("state") || "";
    const code = params.get("code");
    const accessToken = hashParams.get("access_token");
    const error = params.get("error") || hashParams.get("error");

    if (error) {
      setStatus("error");
      setDetail(`Discord: ${error}`);
      return;
    }

    const payload = { state };
    if (accessToken) payload.access_token = accessToken;
    else if (code) payload.code = code;
    else {
      setStatus("error");
      setDetail("No OAuth data returned by Discord.");
      return;
    }

    api.post("/verify/complete", payload)
      .then(r => {
        setStatus("ok");
        setMessage(r.data.message || "You're verified — welcome in.");
        setDetail(r.data.already_verified ? "You already had the verified role — nothing to do." : null);
      })
      .catch(err => {
        setStatus("error");
        setDetail(err?.response?.data?.detail || "Verification failed.");
      });
  }, [params, location.hash]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white flex items-center justify-center px-6" data-testid="verify-callback">
      <div className="max-w-md w-full border border-white/10 bg-[#0D0E15] p-8 space-y-5 rounded-md">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-md flex items-center justify-center border ${
            status === "ok" ? "border-[#10B981]/50" :
            status === "error" ? "border-[#EF4444]/50" :
            "border-[#8B5CF6]/50"
          }`}>
            {status === "ok"    && <CheckCircle2 className="w-6 h-6 text-[#10B981]" />}
            {status === "error" && <XCircle       className="w-6 h-6 text-[#EF4444]" />}
            {status === "working" && <Loader2 className="w-6 h-6 text-[#8B5CF6] animate-spin" />}
          </div>
          <div>
            <div className="font-display font-black text-xl">
              {status === "ok" && "Verified"}
              {status === "error" && "Verification Failed"}
              {status === "working" && "Verifying…"}
            </div>
            <div className="font-mono text-[10px] tracking-widest text-[#64748B] uppercase flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> ModNode
            </div>
          </div>
        </div>

        {status === "ok" && (
          <div className="border border-[#10B981]/30 bg-[#10B981]/5 p-4 rounded-md space-y-2">
            <div className="text-sm text-[#E2E8F0]">{message}</div>
            {detail && <div className="font-mono text-[10px] text-[#64748B]">// {detail}</div>}
            <div className="pt-2">
              <a
                href="https://discord.com/app"
                className="inline-block text-[#8B5CF6] text-sm hover:underline font-display font-bold"
                data-testid="verify-back-to-discord"
              >
                Return to Discord →
              </a>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="border border-[#EF4444]/40 bg-[#EF4444]/10 p-4 rounded-md space-y-2">
            <div className="text-sm text-[#EF4444] font-mono">{detail}</div>
            <div className="pt-2">
              <Link to="/" className="inline-block text-[#94A3B8] text-sm hover:underline">
                Back to home →
              </Link>
            </div>
          </div>
        )}

        {status === "working" && (
          <div className="text-sm text-[#94A3B8]">Talking to Discord…</div>
        )}
      </div>
    </div>
  );
}
